import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "../utils/toast-notification";
import ApiClient from "../utils/api-client";
import { apiErrorMessage } from "../utils/api-error";
import { Logo } from "../components/Logo";
import BillingForm from "./billing/BillingForm";
import { BillingFields, emptyBilling, fieldClass, PRIVACY_POLICY_VERSION } from "./billing/billing";

const Register = () => {
  const { t } = useTranslation();
  const [plan, setPlan] = useState("free");
  const [billing, setBilling] = useState<BillingFields>(emptyBilling());
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const honeypot = React.useRef<HTMLInputElement>(null);

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    const password = String(data.get("password") || "");
    if (password !== String(data.get("password_repeat") || "")) {
      toast.error(t("register.password-mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const response = await ApiClient.register({
        name: String(data.get("name") || ""),
        email: String(data.get("email") || ""),
        password,
        plan,
        privacy_policy_version: PRIVACY_POLICY_VERSION,
        privacy_consent: data.get("privacy_consent") === "on",
        company_website: honeypot.current?.value || "",
        salutation: plan === "paid" ? billing.salutation : "",
        company: plan === "paid" ? billing.company : "",
        street: plan === "paid" ? billing.street : "",
        postal_code: plan === "paid" ? billing.postal_code : "",
        city: plan === "paid" ? billing.city : "",
        country: plan === "paid" ? billing.country : "",
        vat_id: plan === "paid" ? billing.vat_id : "",
        billing_email: plan === "paid" ? billing.billing_email : "",
        project_count: plan === "paid" ? Number(billing.project_count) : 0,
        message: plan === "paid" ? billing.message : "",
      });
      toast.success(response?.data?.message || t("register.sent"));
      setSent(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, t("register.failed")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Logo className="max-w-full" />
        <h2 className="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900">
          {t("register.header")}
        </h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {sent ? (
            <p className="text-sm text-gray-700">{t("register.sent")}</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-gray-700" htmlFor="name">
                {t("register.name")}
              </label>
              <input id="name" name="name" required className={`${fieldClass} mt-1`} />

              <label className="block text-sm font-medium text-gray-700 mt-6" htmlFor="email">
                {t("login.email")}
              </label>
              <input id="email" name="email" type="email" required className={`${fieldClass} mt-1`} />

              <label className="block text-sm font-medium text-gray-700 mt-6" htmlFor="password">
                {t("login.password")}
              </label>
              <input id="password" name="password" type="password" required minLength={8} className={`${fieldClass} mt-1`} />

              <label className="block text-sm font-medium text-gray-700 mt-6" htmlFor="password_repeat">
                {t("register.password-repeat")}
              </label>
              <input id="password_repeat" name="password_repeat" type="password" required minLength={8} className={`${fieldClass} mt-1`} />

              <fieldset className="mt-6">
                <legend className="text-sm font-medium text-gray-700">{t("register.plan")}</legend>
                <label className="mt-2 flex items-center text-sm text-gray-700">
                  <input type="radio" name="plan" className="mr-2" checked={plan === "paid"} onChange={() => setPlan("paid")} />
                  {t("register.plan-paid")}
                </label>
                <label className="mt-2 flex items-center text-sm text-gray-700">
                  <input type="radio" name="plan" className="mr-2" checked={plan === "free"} onChange={() => setPlan("free")} />
                  {t("register.plan-free")}
                </label>
              </fieldset>

              {plan === "paid" && (
                <div className="mt-6">
                  <BillingForm value={billing} onChange={setBilling} />
                </div>
              )}

              <label className="mt-6 flex items-start text-sm text-gray-700">
                <input type="checkbox" name="privacy_consent" required className="mt-1 mr-2" />
                <span>
                  {t("register.privacy-before")}{" "}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noreferrer">
                    {t("register.privacy-link")}
                  </Link>{" "}
                  ({PRIVACY_POLICY_VERSION}){" "}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noreferrer">
                    searchanalyzer.io
                  </Link>
                </span>
              </label>

              <div className="absolute left-0 top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="company_website">Website</label>
                <input id="company_website" ref={honeypot} type="text" tabIndex={-1} autoComplete="off" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500"
              >
                {t("register.submit")}
              </button>
            </form>
          )}
          <p className="mt-6 text-center text-sm">
            <Link to="/" className="text-indigo-600 hover:text-indigo-500">
              {t("register.back-to-login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
